<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  scheduledMessage: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['update:show', 'close', 'updated']);

const { t } = useI18n();
const store = useStore();

const content = ref('');
const scheduledAt = ref('');
const recurrenceType = ref('');
const recurrenceEndAt = ref('');
const isSubmitting = ref(false);

const localShow = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});

const minDateTime = computed(() => {
  const now = new Date();
  now.setMinutes(now.getMinutes() + 5);
  return now.toISOString().slice(0, 16);
});

const datePresets = computed(() => [
  {
    key: 'IN_1_HOUR',
    label: t('SCHEDULED_MESSAGES.FORM.SCHEDULED_AT.PRESETS.IN_1_HOUR'),
    getValue: () => {
      const date = new Date();
      date.setHours(date.getHours() + 1);
      return date;
    },
  },
  {
    key: 'IN_2_HOURS',
    label: t('SCHEDULED_MESSAGES.FORM.SCHEDULED_AT.PRESETS.IN_2_HOURS'),
    getValue: () => {
      const date = new Date();
      date.setHours(date.getHours() + 2);
      return date;
    },
  },
  {
    key: 'TOMORROW_9AM',
    label: t('SCHEDULED_MESSAGES.FORM.SCHEDULED_AT.PRESETS.TOMORROW_9AM'),
    getValue: () => {
      const date = new Date();
      date.setDate(date.getDate() + 1);
      date.setHours(9, 0, 0, 0);
      return date;
    },
  },
  {
    key: 'TOMORROW_2PM',
    label: t('SCHEDULED_MESSAGES.FORM.SCHEDULED_AT.PRESETS.TOMORROW_2PM'),
    getValue: () => {
      const date = new Date();
      date.setDate(date.getDate() + 1);
      date.setHours(14, 0, 0, 0);
      return date;
    },
  },
]);

const applyPreset = preset => {
  const date = preset.getValue();
  scheduledAt.value = date.toISOString().slice(0, 16);
};

const recurrenceOptions = computed(() => [
  { value: '', label: t('SCHEDULED_MESSAGES.LIST.RECURRENCE.NONE') },
  { value: 'daily', label: t('SCHEDULED_MESSAGES.LIST.RECURRENCE.DAILY') },
  { value: 'weekly', label: t('SCHEDULED_MESSAGES.LIST.RECURRENCE.WEEKLY') },
  { value: 'monthly', label: t('SCHEDULED_MESSAGES.LIST.RECURRENCE.MONTHLY') },
]);

const hasAttachments = computed(() => {
  return (
    props.scheduledMessage?.has_attachments ||
    props.scheduledMessage?.attachments_count > 0
  );
});

const isFormValid = computed(() => {
  const hasContent = content.value.trim().length > 0;
  const hasSchedule = !!scheduledAt.value;
  // Valid if: (has content OR has attachments) AND has schedule
  return (hasContent || hasAttachments.value) && hasSchedule;
});

const nextOccurrences = computed(() => {
  if (!scheduledAt.value || !recurrenceType.value) return [];

  const baseDate = new Date(scheduledAt.value);
  const occurrences = [];

  for (let i = 1; i <= 3; i += 1) {
    const nextDate = new Date(baseDate);

    switch (recurrenceType.value) {
      case 'daily':
        nextDate.setDate(nextDate.getDate() + i);
        break;
      case 'weekly':
        nextDate.setDate(nextDate.getDate() + i * 7);
        break;
      case 'monthly':
        nextDate.setMonth(nextDate.getMonth() + i);
        break;
      default:
        break;
    }

    if (recurrenceEndAt.value) {
      const endDate = new Date(recurrenceEndAt.value);
      if (nextDate > endDate) break;
    }

    occurrences.push(nextDate);
  }

  return occurrences;
});

const formatPreviewDate = date => {
  return date.toLocaleDateString('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDateForInput = timestamp => {
  if (!timestamp) return '';
  const date = new Date(timestamp * 1000);
  return date.toISOString().slice(0, 16);
};

const formatDateForDateInput = timestamp => {
  if (!timestamp) return '';
  const date = new Date(timestamp * 1000);
  return date.toISOString().slice(0, 10);
};

watch(
  () => props.show,
  newVal => {
    if (newVal && props.scheduledMessage) {
      content.value = props.scheduledMessage.content || '';
      scheduledAt.value = formatDateForInput(
        props.scheduledMessage.scheduled_at
      );
      recurrenceType.value = props.scheduledMessage.recurrence_type || '';
      recurrenceEndAt.value = formatDateForDateInput(
        props.scheduledMessage.recurrence_end_at
      );
    }
  }
);

const onClose = () => {
  emit('close');
  emit('update:show', false);
};

const onUpdate = async () => {
  if (!isFormValid.value || isSubmitting.value || !props.scheduledMessage)
    return;

  isSubmitting.value = true;

  try {
    const data = {
      id: props.scheduledMessage.id,
      scheduled_message: {
        content: content.value,
        scheduled_at: new Date(scheduledAt.value).toISOString(),
        recurrence_type: recurrenceType.value || null,
        recurrence_end_at: recurrenceEndAt.value
          ? new Date(recurrenceEndAt.value).toISOString()
          : null,
        status: 'pending',
      },
    };

    const updatedMessage = await store.dispatch(
      'scheduledMessages/update',
      data
    );
    useAlert(t('SCHEDULED_MESSAGES.API.UPDATE_SUCCESS'));
    emit('updated', updatedMessage);
    onClose();
  } catch (error) {
    useAlert(t('SCHEDULED_MESSAGES.API.ERROR_MESSAGE'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="onClose">
    <woot-modal-header
      :header-title="t('SCHEDULED_MESSAGES.EDIT.TITLE')"
      :header-content="t('SCHEDULED_MESSAGES.EDIT.DESC')"
    />
    <div class="flex flex-col gap-5 p-6">
      <!-- Attachment Indicator -->
      <div
        v-if="hasAttachments"
        class="flex items-center gap-2 p-3 rounded-lg bg-n-alpha-2 border border-n-weak"
      >
        <span class="i-ph-paperclip text-n-slate-11" />
        <span class="text-sm text-n-slate-11">
          {{
            t('SCHEDULED_MESSAGES.EDIT.HAS_ATTACHMENTS', {
              count: scheduledMessage?.attachments_count || 1,
            })
          }}
        </span>
      </div>

      <!-- Message Field -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('SCHEDULED_MESSAGES.FORM.CONTENT.LABEL') }}
          <span v-if="hasAttachments" class="text-n-slate-10 font-normal">
            ({{ t('SCHEDULED_MESSAGES.EDIT.OPTIONAL_WITH_ATTACHMENT') }})
          </span>
        </label>
        <textarea
          v-model="content"
          class="w-full min-h-[120px] px-3 py-3 text-sm text-n-slate-12 placeholder-n-slate-10 bg-n-alpha-black3 border border-n-strong rounded-lg resize-none transition-colors focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent"
          :placeholder="t('SCHEDULED_MESSAGES.FORM.CONTENT.PLACEHOLDER')"
        />
      </div>

      <!-- Schedule Date/Time Field -->
      <div class="flex flex-col gap-2">
        <label
          class="text-sm font-medium text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-ph-calendar-check text-n-slate-11" />
          {{ t('SCHEDULED_MESSAGES.FORM.SCHEDULED_AT.LABEL') }}
        </label>
        <input
          v-model="scheduledAt"
          type="datetime-local"
          :min="minDateTime"
          :aria-label="t('SCHEDULED_MESSAGES.FORM.SCHEDULED_AT.LABEL')"
          class="w-full px-3 py-3 text-sm text-n-slate-12 bg-n-alpha-black2 border border-n-weak rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent"
        />
        <div class="flex flex-wrap gap-2">
          <span class="text-xs text-n-slate-10">
            {{ t('SCHEDULED_MESSAGES.FORM.SCHEDULED_AT.PRESETS.LABEL') }}:
          </span>
          <button
            v-for="preset in datePresets"
            :key="preset.key"
            type="button"
            class="px-2 py-0.5 text-xs rounded-md bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3 hover:text-n-slate-12 transition-colors"
            @click="applyPreset(preset)"
          >
            {{ preset.label }}
          </button>
        </div>
      </div>

      <!-- Recurrence Field -->
      <div class="flex flex-col gap-2">
        <label
          class="text-sm font-medium text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-ph-repeat text-n-slate-11" />
          {{ t('SCHEDULED_MESSAGES.FORM.RECURRENCE.LABEL') }}
        </label>
        <select
          v-model="recurrenceType"
          class="w-full px-3 py-3 text-sm text-n-slate-12 bg-n-alpha-black2 border border-n-weak rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent cursor-pointer"
        >
          <option
            v-for="option in recurrenceOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </div>

      <!-- Recurrence Preview -->
      <div
        v-if="recurrenceType && nextOccurrences.length > 0"
        class="p-3 rounded-lg bg-n-alpha-2 border border-n-weak"
      >
        <p
          class="text-xs font-medium text-n-slate-11 mb-2 flex items-center gap-1.5"
        >
          <span class="i-ph-calendar" />
          {{ t('SCHEDULED_MESSAGES.FORM.RECURRENCE_PREVIEW.TITLE') }}
        </p>
        <ul class="space-y-1">
          <li
            v-for="(date, index) in nextOccurrences"
            :key="index"
            class="text-sm text-n-slate-12 flex items-center gap-2"
          >
            <span class="size-1.5 rounded-full bg-n-brand" />
            {{ formatPreviewDate(date) }}
          </li>
        </ul>
      </div>

      <!-- Recurrence End Date (conditional) -->
      <div v-if="recurrenceType" class="flex flex-col gap-2">
        <label
          class="text-sm font-medium text-n-slate-12 flex items-center gap-2"
        >
          <span class="i-ph-calendar-x text-n-slate-11" />
          {{ t('SCHEDULED_MESSAGES.FORM.RECURRENCE_END.LABEL') }}
        </label>
        <input
          v-model="recurrenceEndAt"
          type="date"
          :min="scheduledAt ? scheduledAt.split('T')[0] : ''"
          :aria-label="t('SCHEDULED_MESSAGES.FORM.RECURRENCE_END.LABEL')"
          class="w-full px-3 py-3 text-sm text-n-slate-12 bg-n-alpha-black2 border border-n-weak rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-n-brand focus:border-transparent"
        />
      </div>

      <!-- Action Buttons -->
      <div class="flex justify-end gap-3 pt-4 mt-2 border-t border-n-strong">
        <NextButton
          :label="t('SCHEDULED_MESSAGES.FORM.CANCEL')"
          slate
          faded
          md
          @click="onClose"
        />
        <NextButton
          :label="t('SCHEDULED_MESSAGES.FORM.UPDATE')"
          icon="i-ph-check"
          blue
          solid
          md
          :disabled="!isFormValid || isSubmitting"
          :is-loading="isSubmitting"
          @click="onUpdate"
        />
      </div>
    </div>
  </woot-modal>
</template>
